import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Breadcrumb, Col, Modal, Row } from 'antd';
import { FormattedMessage } from 'react-intl';
import { RBHBox, RBHText, TagCount } from '../../../../shared/components';
import styleText from '../../../../shared/components/rbh-text/styles';
import { Colors } from '../../../../shared/theme';
import { textStyles } from '../../../../shared/theme/fonts-ppv';
import { ModifierGroup, ModifierGroupType } from '../redux/modifier-group-type';
import { ModifierItem } from '../../ModifierItem/redux/modifier-item-type';
import RBHTable2 from '../../../../shared/components/rbh-table-2';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { modifierGroupCreators } from '../redux/modifier-group-action';
import { MODIFIER_GROUP_KEY, modifierGroupStateSelector } from '../redux/modifier-group-reducer';
import { generalCreators } from '../../../../redux/general/general-action';
import { GENERAL_STATE_KEY } from '../../../../redux/general/general-reducer';
import { MODAL_TYPE } from '../../../../redux/general/general-constant';

const Title = ({ text }: { text: string }) => (
    <RBHText style={{ ...textStyles.subtitle3, color: Colors.grey400 }} text={text} />
);

const Value = ({ text }: { text: string }) => (
    <RBHText style={{ ...textStyles.subtitle1, color: Colors.grey800 }} text={text} />
);

const ModifierGroupDetailScreen = () => {
    const history = useHistory();
    const location = useLocation<{ group: ModifierGroup }>();
    const dispatch = useDispatch();
    const group = location.state?.group;
    const isDeleteSuccess = useSelector(modifierGroupStateSelector(MODIFIER_GROUP_KEY.IS_DELETE_SUCCESS));
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);

    useEffect(() => {
        if (isDeleteSuccess) {
            dispatch(modifierGroupCreators.setModifierGroupState(MODIFIER_GROUP_KEY.IS_DELETE_SUCCESS, false));
            history.replace('/main/modifier-group');
        }
    }, [isDeleteSuccess]);

    if (!group) {
        history.replace('/main/modifier-group');
        return null;
    }

    const handleDeleteGroup = () => {
        dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
            visible: true,
            onOk: onDeleteConfirm,
            modalTitle: 'main.menu.modifieritem.delete.confirm.title',
            modalContent: 'main.menu.modifieritem.delete.confirm.content',
            type: MODAL_TYPE.DELETE,
            confirmButtonStyle: { background: Colors.red600, borderColor: Colors.red600 },
        }));
    }

    const onDeleteConfirm = () => {
        dispatch(modifierGroupCreators.callDeleteModifierGroup(group.menuModifierGroupId));
    };

    const typeLabel: Record<ModifierGroupType, string> = {
        [ModifierGroupType.OPTIONAL]: 'main.menu.modifiergroup.type.optional',
        [ModifierGroupType.SELECT_ONE]: 'main.menu.modifiergroup.type.select_one',
        [ModifierGroupType.MULTIPLE]: 'main.menu.modifiergroup.type.multiple',
    };
    const isOptional = group.menuModifierGroupType === ModifierGroupType.OPTIONAL;

    const tableData = (group.menuModifierItems ?? []).map((item: ModifierItem) => ({
        ...item,
        id: item.menuModifierItemId,
    }));

    return (
        <RBHBox>
            {/* Breadcrumb */}
            <Breadcrumb style={{ ...styleText.breadCrumb, marginBottom: 16 }}>
                <Breadcrumb.Item>
                    <a
                        style={styleText.breadCrumbItemLink}
                        onClick={() => history.push('/main/modifier-group')}
                    >
                        <FormattedMessage id="main.menu.modifiergroup.title" />
                    </a>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <span style={styleText.breadCrumbItem}>{group.menuModifierGroupName}</span>
                </Breadcrumb.Item>
            </Breadcrumb>

            {/* Header */}
            <RBHBox style={{ borderBottom: '1px solid #FBFBFD', paddingBottom: '16px', marginBottom: '16px' }}>
                <RBHText style={styleText.headerText} text={'main.menu.modifiergroup.detail.title'} />
            </RBHBox>

            {/* Info section */}
            <RBHBox
                style={{
                    background: Colors.white,
                    borderRadius: 12,
                    padding: '24px',
                    marginBottom: 24,
                    border: `1px solid ${Colors.grey100}`,
                }}
            >
                <Row gutter={24}>
                    <Col span={5}>
                        <RBHBox style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <Title text={'main.menu.modifiergroup.detail.name'} />
                            <Value text={group.menuModifierGroupName} />
                        </RBHBox>
                    </Col>
                    <Col span={5}>
                        <RBHBox style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <Title text={'main.menu.modifiergroup.detail.nameEN'} />
                            <Value text={group.menuModifierGroupNameEN || '-'} />
                        </RBHBox>
                    </Col>
                    <Col span={5}>
                        <RBHBox style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <Title text={'main.menu.modifiergroup.detail.condition'} />
                            <span
                                style={{
                                    display: 'inline-block',
                                    padding: '4px 6px',
                                    borderRadius: '24px',
                                    ...textStyles.body3,
                                    background: isOptional ? Colors.grey100 : Colors.yellow100,
                                    color: isOptional ? Colors.grey800 : Colors.yellow800,
                                    width: 'fit-content',
                                }}
                            >
                                <FormattedMessage id={typeLabel[group.menuModifierGroupType]} />
                            </span>
                        </RBHBox>
                    </Col>
                    <Col span={5}>
                        <RBHBox style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <Title text={'main.menu.modifiergroup.detail.max'} />
                            <Value text={String(group.menuModifierGroupMax)} />
                        </RBHBox>
                    </Col>
                    <Col span={4} style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <EditOutlined
                            data-testid="modifier-group-detail-edit"
                            translate={null}
                            style={{ color: Colors.grey600, fontSize: 18, cursor: 'pointer' }}
                            onClick={() => history.push(`/main/modifier-group/${group.menuModifierGroupId}/edit`, { group })}
                        />
                        <DeleteOutlined
                            data-testid="modifier-group-detail-delete"
                            translate={null}
                            style={{ color: Colors.grey600, fontSize: 18, marginLeft: 8, cursor: 'pointer' }}
                            onClick={handleDeleteGroup}
                        />
                    </Col>
                </Row>
            </RBHBox>

            <Modal
                visible={deleteModalVisible}
                title={<FormattedMessage id="main.menu.modifiergroup.detail.delete.title" />}
                okText={<FormattedMessage id="main.menu.modifiergroup.detail.delete.confirm" />}
                cancelText={<FormattedMessage id="main.menu.modifiergroup.detail.delete.cancel" />}
                okButtonProps={{ danger: true }}
                onOk={onDeleteConfirm}
                onCancel={() => setDeleteModalVisible(false)}
            >
                <FormattedMessage
                    id="main.menu.modifiergroup.detail.delete.content"
                    values={{ name: group.menuModifierGroupName }}
                />
            </Modal>

            {/* Items table */}
            <RBHBox style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Row justify='space-between' align='middle' style={{ gap: 8, width: '100%' }}>
                    <Row align='middle' style={{ gap: 8 }}>
                        <RBHText
                            style={{ ...textStyles.h4, color: Colors.grey800 }}
                            text="main.menu.modifiergroup.detail.items"
                        />
                        <TagCount id="modifier-group-items-count" count={group.menuModifierItems?.length ?? 0} />
                    </Row>
                </Row>

                <RBHTable2
                    dataSource={tableData}
                    pagination={false}
                    scroll={{ y: 'calc(100vh - 520px)', x: 'auto' }}
                    columns={[
                        {
                            title: <FormattedMessage id="main.menu.modifieritem.column.name" />,
                            dataIndex: 'menuModifierItemName',
                        },
                        {
                            title: <FormattedMessage id="main.menu.modifieritem.column.nameEN" />,
                            dataIndex: 'menuModifierItemNameEN',
                        },
                        {
                            title: <FormattedMessage id="main.menu.modifieritem.column.price" />,
                            dataIndex: 'menuModifierItemPrice',
                        },
                        {
                            title: <FormattedMessage id="main.menu.modifieritem.column.sku_id" />,
                            dataIndex: 'menuModifierItemSKUId',
                        },
                    ]}
                />
            </RBHBox>
        </RBHBox>
    );
};

export default ModifierGroupDetailScreen;
